import React, { Fragment, useContext, useState, useEffect } from 'react';
import axios from 'axios';

// Module Imports.
import { Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography, TextField, Button, CircularProgress, Autocomplete, Chip } from '@mui/material';
import FeatherIcon from 'feather-icons-react';
import UploadIcon from "@mui/icons-material/Upload";

// Components import.
import { ViewContext, Card, AccountNav, Table, Button as GravityButton, useAPI, TitleRow, Message } from 'components/lib';
import { LoadingBackdrop } from 'components/global/LoadingBackdrop/LoadingBackdrop';
import Styles from './account.module.scss';

const INITIAL_FORM = {
    name: '',
    descr: '',
    total_score: 10,
    lab_list: [],
};

const INITIAL_ERRORS = {
    icon: '',
    name: '',
    descr: '',
    total_score: '',
    lab_list: '',
};

export function LearningPath(props) {


    const context = useContext(ViewContext);

    const [accountLPathData, setAccountLPathData] = useState({});
    const [lPathAction, setLPathAction] = useState({ show: false, action: "" });
    const [editId, setEditId] = useState(null);

    // Form state
    const [formData, setFormData] = useState(INITIAL_FORM);
    const [errors, setErrors] = useState(INITIAL_ERRORS);
    const [uploadedIconUrl, setUploadedIconUrl] = useState('');
    const [uploading, setUploading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const { data, loading } = useAPI('/api/account/lpath');
    const MEDIA_URL = process.env.REACT_APP_GLOBAL_MEDIA_URL;
    const [confirmDeleteIcon, setConfirmDeleteIcon] = useState(false);

    // Function to format the date.
    function formateDate(date) {
        const dateObj = date ? new Date(date) : null;
        //Format the Date object into "Aug DD YYYY" format
        const options = { year: 'numeric', month: 'short', day: 'numeric' };
        return dateObj && dateObj.toLocaleDateString('en-US', options);
    }

    // Update the lab data id and name.
    useEffect(() => {

        setAccountLPathData(data);

    }, [data]);

    // ── Open create dialog ──
    function openCreate() {
        setFormData(INITIAL_FORM);
        setUploadedIconUrl('');
        setErrors(INITIAL_ERRORS);
        setEditId(null);
        setLPathAction({ show: true, action: "create" });
    }

    // ── Open edit dialog ──
    function openEdit(data) {
        setFormData({
            name: data.name || '',
            descr: data.descr || '',
            total_score: data.total_score || 10,
            lab_list: data.labData || [],
        });
        setUploadedIconUrl(data.icon || '');
        setErrors(INITIAL_ERRORS);
        setEditId(data.id);
        setLPathAction({ show: true, action: "edit" });
    }

    function handleClose() {
        setLPathAction({ show: false, action: "" });
        setFormData(INITIAL_FORM);
        setUploadedIconUrl('');
        setErrors(INITIAL_ERRORS);
        setEditId(null);
    }

    // ── Field change handler ──
    function handleField(e) {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        setErrors(prev => ({ ...prev, [name]: '' }));
    }

    // ── Icon upload ──
    async function handleIconUpload(file) {
        const allowedTypes = ["image/jpeg", "image/png"];
        if (!allowedTypes.includes(file.type)) {
            context.notification.show("Only JPG and PNG files are allowed", "error", true);
            return;
        }

        const maxSizeKB = 60;
        if (file.size > maxSizeKB * 1024) {
            context.notification.show(`Image must be smaller than ${maxSizeKB}KB`, "error", true);
            return;
        }

        const formData = new FormData();
        formData.append("icon", file);
        setUploading(true);

        try {
            const response = await axios.post('/api/account/lpath/upload', formData);
            const relativePath = response.data.url.replace(/^https?:\/\/[^/]+/, '');
            setUploadedIconUrl(relativePath);
            setErrors(prev => ({ ...prev, icon: '' }));
            context.notification.show("Image uploaded successfully", "success", true);
        } catch (err) {
            context.notification.show("Upload failed", "error", true);
        } finally {
            setUploading(false);
        }
    }

    // ── Validate all fields ──
    function validate() {
        const newErrors = { ...INITIAL_ERRORS };
        let valid = true;

        if (!uploadedIconUrl) {
            newErrors.icon = props.t('account.LearningPath.form.createAndUpdate.field.icon.error');
            valid = false;
        }
        if (!formData.name.trim()) {
            newErrors.name = props.t('account.LearningPath.form.createAndUpdate.field.name.error');
            valid = false;
        }
        if (!formData.descr.trim()) {
            newErrors.descr = props.t('account.LearningPath.form.createAndUpdate.field.descr.error');
            valid = false;
        }
        if (!formData.total_score) {
            newErrors.total_score = props.t('account.LearningPath.form.createAndUpdate.field.totalScore.error');
            valid = false;
        }
        if (!formData.lab_list?.length) {
            newErrors.lab_list = props.t('account.LearningPath.form.createAndUpdate.field.labList.error');
            valid = false;
        }

        setErrors(newErrors);
        return valid;
    }

    // ── Submit ──
    async function handleSubmit() {
        if (!validate()) return;

        setSubmitting(true);
        try {
            const isCreate = lPathAction.action === "create";
            const res = await axios({
                method: isCreate ? 'POST' : 'PATCH',
                url: '/api/account/lpath',
                data: {
                    cert_id: editId || null,
                    name: formData.name,
                    descr: formData.descr,
                    icon: uploadedIconUrl,
                    total_score: formData.total_score,
                    lab_list: formData.lab_list,
                },
            });

            const message = res?.data?.message || (
                isCreate
                    ? props.t('account.LearningPath.form.createAndUpdate.notification.create')
                    : props.t('account.LearningPath.form.createAndUpdate.notification.update')
            );

            context.notification.show(message, "success", true);
            handleClose();

            setTimeout(() => {
                if (res?.data?.reload) window.location.reload();
            }, 350);

        } catch (err) {
            context.handleError(err);
        } finally {
            setSubmitting(false);
        }
    }

    function deleteLPath(data) {

        try {

            // Check if the data is an array.
            const multi = Array.isArray(data);
            // Ensure that all details are in array format.
            const ids = multi ? data.map(item => item?.row?.id) : [data.id];
            context.modal.show({
                title: props.t('account.LearningPath.form.delete.title'),
                form: {
                    ids: {
                        type: 'hidden',
                        value: ids,
                    }
                },
                buttonText: "delete",
                text: props.t('account.LearningPath.form.delete.text'),
                url: '/api/account/lpath',
                destructive: true,
                method: 'DELETE',
            }, () => {
                setTimeout(() =>
                    window.location.reload()
                    , 350);
                context.notification.show(props.t('account.LearningPath.form.delete.notification'), "success", true);
            }
            );
        }
        catch (err) {
            context.handleError(err);
        }
    }

    return (
        <Fragment>
            {loading ?
                <LoadingBackdrop isOpen={true} /> :
                <>
                    <AccountNav />
                    <Message
                        closable
                        title={props.t('account.LearningPath.message.title')}
                        text={props.t('account.LearningPath.message.text')}
                        type="info"
                    />

                    <div>
                        <TitleRow title={props.t('account.LearningPath.table.title')}>
                            <div style={{ display: "flex", gap: "0.7em" }}>
                                <GravityButton small text={props.t('account.LearningPath.create')} action={openCreate} />
                            </div>
                        </TitleRow>

                        <Card className={Styles.cardContainer}>
                            <Table
                                search
                                selectable
                                className='restrict-width'
                                // data={}
                                data={accountLPathData?.lpath_data?.map(x => ({
                                    ...x,
                                    created_at: formateDate(x.created_at)
                                }
                                ))}
                                loading={loading}
                                hide={["id", "labs", "icon"]}
                                show={['created_at', 'name', 'descr', 'total_score']}
                                actions={{
                                    delete: deleteLPath,
                                    edit: openEdit
                                }}
                                bulkActions={{
                                    delete: deleteLPath,
                                }}
                                translation='account.LearningPath.table'
                            />
                        </Card>
                    </div>

                    <Dialog open={lPathAction?.show} onClose={handleClose} fullWidth maxWidth="md" disableEscapeKeyDown>
                        {/* Header */}
                        <Box sx={{
                            display: 'flex', justifyContent: 'flex-start',
                            padding: '20px', backgroundColor: '#f2f2f2'
                        }}>
                            <FeatherIcon
                                icon="x" size='24px'
                                style={{ color: '#000', fontWeight: 'bold', cursor: 'pointer' }}
                                onClick={handleClose}
                            />
                        </Box>

                        <DialogTitle sx={{ fontWeight: 'bold', fontSize: '24px' }}>
                            {lPathAction?.action === "create"
                                ? props.t('account.LearningPath.form.createAndUpdate.title.create')
                                : props.t('account.LearningPath.form.createAndUpdate.title.update')}
                        </DialogTitle>

                        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: '16px !important' }}>

                            {/* ── Icon Upload ── */}
                            <Box>
                                <Typography sx={{
                                    fontSize: "12px", fontWeight: 600,
                                    textTransform: "uppercase", letterSpacing: "0.04em",
                                    color: "#374151", mb: 1
                                }}>
                                    {props.t('account.LearningPath.form.createAndUpdate.field.icon.label')}
                                    <span style={{ color: "#ef4444" }}> *</span>
                                </Typography>

                                <Box
                                    onClick={() => !uploading && !uploadedIconUrl && document.getElementById("muiFileUpload").click()}
                                    onDragOver={(e) => e.preventDefault()}
                                    onDrop={(e) => {
                                        e.preventDefault();
                                        const file = e.dataTransfer.files[0];
                                        if (file && !uploadedIconUrl) handleIconUpload(file);
                                    }}
                                    sx={{
                                        border: `1px dashed ${errors.icon ? "#ef4444" : "#d1d5db"}`,
                                        borderRadius: "8px",
                                        p: 3,
                                        textAlign: "center",
                                        cursor: uploading ? "wait" : uploadedIconUrl ? "default" : "pointer",
                                        backgroundColor: "#fafafa",
                                        transition: "0.2s",
                                        "&:hover": { borderColor: uploadedIconUrl ? "#d1d5db" : "#9ca3af" }
                                    }}
                                >
                                    {uploading
                                        ? <CircularProgress size={24} sx={{ color: "#6b7280" }} />
                                        : <>
                                            <UploadIcon sx={{ color: errors.icon ? "#ef4444" : "#6b7280", mb: 1 }} />
                                            <Typography sx={{ fontSize: "13px", color: errors.icon ? "#ef4444" : "#6b7280" }}>
                                                Drop image or click to upload
                                            </Typography>
                                        </>
                                    }
                                    <input
                                        id="muiFileUpload"
                                        type="file"
                                        accept="image/png, image/jpeg"
                                        hidden
                                        onChange={(e) => {
                                            const file = e.target.files[0];
                                            if (!file) return;
                                            if (uploadedIconUrl) {
                                                context.notification.show("Remove the current icon first", "error", true);
                                                return;
                                            }
                                            handleIconUpload(file);
                                        }}
                                    />
                                </Box>

                                {/* Icon error */}
                                {errors.icon && (
                                    <Typography sx={{ fontSize: "12px", color: "#ef4444", mt: 0.5, ml: 0.5 }}>
                                        {errors.icon}
                                    </Typography>
                                )}

                                {/* Icon preview */}
                                {uploadedIconUrl && (
                                    <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1 }}>
                                        <Box sx={{ position: "relative" }}>
                                            <img
                                                src={`${MEDIA_URL}${uploadedIconUrl}`}
                                                alt="Uploaded icon preview"
                                                style={{
                                                    width: 48, height: 48, borderRadius: 6,
                                                    objectFit: "cover", border: "1px solid #e5e7eb"
                                                }}
                                            />
                                            <Box
                                                onClick={() => setConfirmDeleteIcon(true)}
                                                sx={{
                                                    position: "absolute", top: -6, right: -6,
                                                    background: "#fff", borderRadius: "50%",
                                                    width: 18, height: 18, display: "flex",
                                                    alignItems: "center", justifyContent: "center",
                                                    cursor: "pointer", boxShadow: "0 0 3px rgba(0,0,0,0.2)"
                                                }}
                                            >✕</Box>
                                        </Box>
                                        <Typography sx={{ fontSize: "12px", color: "#6b7280" }}>
                                            Uploaded
                                        </Typography>
                                    </Box>
                                )}
                            </Box>

                            {/* ── Name ── */}
                            <TextField
                                label={props.t('account.LearningPath.form.createAndUpdate.field.name.label')}
                                name="name"
                                value={formData.name}
                                onChange={handleField}
                                error={!!errors.name}
                                helperText={errors.name}
                                required
                                fullWidth
                                size="small"
                            />

                            {/* ── Description ── */}
                            <TextField
                                label={props.t('account.LearningPath.form.createAndUpdate.field.descr.label')}
                                name="descr"
                                value={formData.descr}
                                onChange={handleField}
                                error={!!errors.descr}
                                helperText={errors.descr}
                                required
                                fullWidth
                                multiline
                                rows={3}
                                size="small"
                            />

                            {/* ── Total Score ── */}
                            <TextField
                                label={props.t('account.LearningPath.form.createAndUpdate.field.totalScore.label')}
                                name="total_score"
                                type="number"
                                value={formData.total_score}
                                onChange={handleField}
                                error={!!errors.total_score}
                                helperText={errors.total_score}
                                required
                                fullWidth
                                size="small"
                            />

                            {/* ── Lab List ── */}
                            <Autocomplete
                                multiple
                                options={accountLPathData?.lab_list || []}
                                getOptionLabel={(option) => option.label || option.value || ''}
                                isOptionEqualToValue={(option, value) => option.value === value.value}
                                value={formData.lab_list}
                                onChange={(_, newValue) => {
                                    setFormData(prev => ({ ...prev, lab_list: newValue }));
                                    setErrors(prev => ({ ...prev, lab_list: '' }));
                                }}
                                renderTags={(value, getTagProps) =>
                                    value.map((option, index) => (
                                        <Chip
                                            label={option.label || option.value}
                                            size="small"
                                            {...getTagProps({ index })}
                                        />
                                    ))
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={props.t('account.LearningPath.form.createAndUpdate.field.labList.label')}
                                        placeholder={props.t('account.LearningPath.form.createAndUpdate.field.labList.placeholder')}
                                        error={!!errors.lab_list}
                                        helperText={errors.lab_list}
                                        required
                                        size="small"
                                    />
                                )}
                            />

                        </DialogContent>

                        {/* ── Submit Button ── */}
                        <DialogActions sx={{ px: 3, pb: 3 }}>
                            <Button
                                onClick={handleSubmit}
                                disabled={submitting || uploading}
                                fullWidth
                                variant="contained"
                                sx={{
                                    backgroundColor: "#16a34a",
                                    "&:hover": { backgroundColor: "#15803d" },
                                    "&:disabled": { backgroundColor: "#86efac" },
                                    py: 1.5,
                                    fontSize: "15px",
                                    fontWeight: 600,
                                    borderRadius: "8px",
                                    textTransform: "none",
                                }}
                            >
                                {submitting
                                    ? <CircularProgress size={20} sx={{ color: "#fff" }} />
                                    : lPathAction?.action === "create"
                                        ? props.t('account.LearningPath.form.createAndUpdate.button.create')
                                        : props.t('account.LearningPath.form.createAndUpdate.button.update')
                                }
                            </Button>
                        </DialogActions>

                    </Dialog>
                    <Dialog
                        open={confirmDeleteIcon}
                        onClose={() => setConfirmDeleteIcon(false)}
                        maxWidth="xs"
                        fullWidth
                    >
                        <DialogTitle>
                            Delete Icon
                        </DialogTitle>

                        <DialogContent>
                            <Typography>
                                Are you sure you want to remove this icon?
                            </Typography>
                        </DialogContent>

                        <DialogActions>

                            <Button
                                onClick={() => setConfirmDeleteIcon(false)}
                            >
                                Cancel
                            </Button>

                            <Button
                                color="error"
                                variant="contained"
                                onClick={async () => {

                                    try {

                                        await axios.delete(
                                            "/api/account/delete-lpath-icon",
                                            {
                                                data: {
                                                    icon: uploadedIconUrl
                                                }
                                            }
                                        );

                                        setUploadedIconUrl("");

                                        context.notification.show(
                                            "Icon removed successfully",
                                            "success",
                                            true
                                        );

                                    } catch (error) {

                                        console.error(
                                            "Failed to delete icon:",
                                            error
                                        );

                                        context.notification.show(
                                            "Failed to delete icon",
                                            "error",
                                            true
                                        );

                                    } finally {

                                        setConfirmDeleteIcon(false);
                                    }
                                }}
                            >
                                Delete
                            </Button>

                        </DialogActions>
                    </Dialog>
                </>
            }
        </Fragment>
    );
}